import { normalizeTag } from './crypto.js';
/**
 * Правила продукта чистыми функциями.
 *
 * Перенесены из однофайлового артефакта дословно: меняется оформление, но не
 * поведение. Общий пакет с сервером и с будущим iOS — одна реализация правил на
 * всю систему (раздел 10 архитектуры).
 *
 * Даты считаются в местном времени: спринт — это календарный отрезок в часовом
 * поясе человека, а не отрезок UTC.
 */
// --------------------------------------------------------------------------
// Даты
// --------------------------------------------------------------------------
function pad(value) {
    return value < 10 ? `0${String(value)}` : String(value);
}
/** Дата в форме ГГГГ-ММ-ДД по местному времени. */
export function toYmd(date) {
    return `${String(date.getFullYear())}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}
export function parseYmd(value) {
    const parts = value.split('-');
    const year = Number(parts[0]);
    const month = Number(parts[1]);
    const day = Number(parts[2]);
    if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
        throw new Error(`неразбираемая дата: ${value}`);
    }
    return new Date(year, month - 1, day);
}
export function addDays(date, days) {
    const shifted = new Date(date.getTime());
    shifted.setDate(shifted.getDate() + days);
    return shifted;
}
/** Понедельник недели, в которую попала дата. */
export function startOfWeek(date) {
    const start = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
    return start;
}
/** Номер недели по ISO 8601 — тот самый, что виден в заголовке «Неделя 37». */
export function isoWeek(date) {
    const thursday = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    thursday.setDate(thursday.getDate() + 3 - ((thursday.getDay() + 6) % 7));
    const firstWeek = new Date(thursday.getFullYear(), 0, 4);
    const days = (thursday.getTime() - firstWeek.getTime()) / 86_400_000;
    return 1 + Math.round((days - 3 + ((firstWeek.getDay() + 6) % 7)) / 7);
}
/** Сегодняшняя дата в форме ГГГГ-ММ-ДД. */
export function todayYmd(now = new Date()) {
    return toYmd(now);
}
// --------------------------------------------------------------------------
// Спринты
// --------------------------------------------------------------------------
/**
 * Спринты не хранятся: ключ считается из даты, поэтому «Неделя 37» существует
 * всегда, её не надо заводить и нельзя закрыть (раздел 3.5 архитектуры).
 */
export function sprintKeyOf(date, type) {
    if (type === 'month') {
        return `m:${String(date.getFullYear())}-${pad(date.getMonth() + 1)}`;
    }
    return `w:${toYmd(startOfWeek(date))}`;
}
export function sprintTypeOf(key) {
    return key.startsWith('m:') ? 'month' : 'week';
}
export function sprintStart(key) {
    if (key.startsWith('w:')) {
        return parseYmd(key.slice(2));
    }
    const parts = key.slice(2).split('-');
    return new Date(Number(parts[0]), Number(parts[1]) - 1, 1);
}
export function sprintEnd(key) {
    const start = sprintStart(key);
    if (key.startsWith('w:')) {
        return addDays(start, 6);
    }
    return new Date(start.getFullYear(), start.getMonth() + 1, 0);
}
export function shiftSprint(key, direction) {
    const start = sprintStart(key);
    if (key.startsWith('w:')) {
        return sprintKeyOf(addDays(start, 7 * direction), 'week');
    }
    return sprintKeyOf(new Date(start.getFullYear(), start.getMonth() + direction, 1), 'month');
}
export function nextSprint(key) {
    return shiftSprint(key, 1);
}
export function prevSprint(key) {
    return shiftSprint(key, -1);
}
/**
 * Задача видна в периоде, если ключи совпали или её собственный период
 * начинается внутри показанного. Второе условие и есть ответ на требование
 * 3.2.5: после смены длины спринта недельные задачи не исчезают из месяца.
 *
 * `sprint = null` — это Входящие, а не отсутствие данных.
 */
export function taskVisibleInSprint(sprint, period) {
    if (sprint === null)
        return period === 'inbox';
    if (period === 'inbox')
        return false;
    if (sprint === period)
        return true;
    const start = sprintStart(sprint).getTime();
    return start >= sprintStart(period).getTime() && start <= sprintEnd(period).getTime();
}
/**
 * Сколько закрытая задача висит во Входящих, прежде чем спрятаться (3.4.5).
 *
 * Две недели — не круглое число, а расстояние между разборами: во Входящие
 * заглядывают раз в несколько дней, и закрытое позавчера должно ещё быть на
 * виду, чтобы не показалось, будто его потеряли.
 */
export const INBOX_DONE_DAYS = 14;
/**
 * Показывать ли закрытую задачу во Входящих.
 *
 * Во Входящих нет недели, которой задача принадлежит, поэтому закрытые копятся
 * там навсегда — в спринте такой беды нет, там закрытая остаётся историей
 * своей недели. Через две недели задача **перестаёт показываться**, но не
 * удаляется: её находит поиск по всем спринтам, и она возвращается в список,
 * если снять отметку «готово».
 *
 * Задача без времени закрытия остаётся видимой: «когда закрыли, неизвестно» —
 * не повод прятать. Такие записи есть у всех, кто закрывал задачи до того, как
 * поле начали заполнять.
 */
export function inboxShowsDone(task, now) {
    if (task.status !== 'done')
        return true;
    if (task.done_at == null)
        return true;
    const closed = Date.parse(task.done_at);
    if (!Number.isFinite(closed))
        return true;
    return now.getTime() - closed < INBOX_DONE_DAYS * 86_400_000;
}
export function dueState(task, today) {
    if (task.due === null)
        return 'none';
    if (task.status === 'done')
        return 'far';
    if (task.due < today)
        return 'overdue';
    if (task.due === today || task.due === toYmd(addDays(parseYmd(today), 1)))
        return 'soon';
    return 'far';
}
/**
 * Время срока, которое можно показывать: без даты его нет.
 *
 * Пара «время есть, даты нет» в записи возможна — слияние идёт по полям, и два
 * устройства способны снять дату и поставить время одновременно. Схема её не
 * отвергает намеренно (см. `taskSchema`), поэтому правило одно и здесь: время
 * читается только вместе с датой.
 */
export function dueTimeOf(task) {
    if (task.due === null)
        return null;
    return task.due_time ?? null;
}
/** Шаг времени срока в минутах: встречу назначают на 10:40, а не на 10:42. */
export const DUE_TIME_STEP_MINUTES = 5;
/**
 * Время срока, приведённое к шагу: до ближайших пяти минут.
 *
 * Правило здесь, а не только в `step` у поля: колесо времени в iOS Safari
 * атрибут `step` не соблюдает и предлагает каждую минуту, а агент через API
 * и вовсе полей не видит. Округление — к ближайшему, половина шага идёт
 * вверх. Конец суток не перекатывается на следующий день: 23:58 становится
 * 23:55, иначе округление меняло бы дату, о которой здесь ничего не известно.
 */
export function snapDueTime(time, step = DUE_TIME_STEP_MINUTES) {
    const [hoursPart, minutesPart] = time.split(':');
    const hours = Number(hoursPart);
    const minutes = Number(minutesPart);
    if (!Number.isInteger(hours) || !Number.isInteger(minutes))
        return time;
    const total = Math.round((hours * 60 + minutes) / step) * step;
    const capped = Math.min(total, 24 * 60 - step);
    return `${pad(Math.floor(capped / 60))}:${pad(capped % 60)}`;
}
/**
 * Задачи со сроком идут выше задач без срока внутри своей группы (3.6.6).
 *
 * Внутри одной даты то же правило на уровень дня: сначала задачи со временем по
 * возрастанию, потом без времени. Время сравнивается строкой — `ЧЧ:ММ` с
 * ведущими нулями сортируется как число.
 */
export function compareByDue(a, b) {
    const left = a.due ?? '9999-99-99';
    const right = b.due ?? '9999-99-99';
    if (left !== right)
        return left < right ? -1 : 1;
    const leftTime = dueTimeOf(a) ?? '99:99';
    const rightTime = dueTimeOf(b) ?? '99:99';
    if (leftTime !== rightTime)
        return leftTime < rightTime ? -1 : 1;
    if (a.order_key !== b.order_key)
        return a.order_key < b.order_key ? -1 : 1;
    if (a.id === b.id)
        return 0;
    return a.id < b.id ? -1 : 1;
}
/**
 * Задачу держит незакрытая зависимость. Метка снаружи — подсказка, а не
 * вахтёр: закрыть заблокированную задачу всё равно можно (3.9.5), поэтому
 * функция ничего не запрещает, а только отвечает на вопрос.
 */
export function isBlocked(task, links, tasks) {
    if (task.status === 'done')
        return false;
    return links.some((link) => {
        if (link.blocked_id !== task.id)
            return false;
        if (link.deleted_at != null)
            return false;
        const blocker = tasks.get(link.blocker_id);
        if (blocker === undefined || blocker.deleted_at != null)
            return false;
        return blocker.status !== 'done';
    });
}
/**
 * Доля закрытых вложенных задач у проекта. Проект — обычная задача, у которой
 * появились вложенные: отдельного типа нет (3.8.1).
 */
export function projectProgress(projectId, tasks) {
    let total = 0;
    let done = 0;
    for (const task of tasks) {
        if (task.parent_id !== projectId || task.deleted_at != null)
            continue;
        total += 1;
        if (task.status === 'done')
            done += 1;
    }
    return { total, done };
}
/** Есть ли у задачи живые вложенные, то есть проект ли она (3.8.1). */
export function hasChildren(taskId, tasks) {
    for (const task of tasks) {
        if (task.parent_id === taskId && task.deleted_at == null)
            return true;
    }
    return false;
}
/**
 * Уровней ровно два: проект и его задачи. Проект не может быть вложенной
 * задачей, а вложенная задача не может стать проектом.
 *
 * Матрёшка любой глубины технически работала и требованиям не противоречила,
 * но решение владельца (этап 12) — ограничиться двумя уровнями: в личном
 * трекере дерево произвольной глубины превращает «зонтик» в структуру, которую
 * приходится держать в голове, а зонтик тем и хорош, что держать в голове
 * ничего не надо.
 *
 * Функции отвечают на два разных вопроса и потому раздельные: «годится ли эта
 * задача в проекты» и «можно ли этой задаче выбрать проект».
 */
export function canBeProject(candidate) {
    // Именно `== null`, а не `=== null`: в локальной копии поле может вообще
    // отсутствовать. Слияние копирует только поля, у которых есть часы в
    // `field_clocks`, поэтому значение, выставленное сервером по умолчанию, до
    // устройства-автора не доезжает (раздел 3.2 архитектуры и разбор этапа 11).
    // Со строгим сравнением такая задача переставала годиться в проекты — то
    // есть список кандидатов пустел ровно там, где данные постарше.
    return candidate.parent_id == null;
}
export function canHaveParent(taskId, tasks) {
    return !hasChildren(taskId, tasks);
}
/**
 * Замкнётся ли цепочка проектов, если у `taskId` родителем станет `parentId`.
 * Задача не может входить сама в себя ни напрямую, ни через цепочку (3.8.3).
 *
 * При двух уровнях цикл невозможен по построению, но проверка остаётся: она
 * страхует данные, заведённые до этого правила, и переживёт его смягчение.
 */
export function wouldCycle(taskId, parentId, parentOf) {
    if (parentId === null)
        return false;
    if (parentId === taskId)
        return true;
    const seen = new Set([taskId]);
    let current = parentId;
    while (current !== null) {
        if (seen.has(current))
            return true;
        seen.add(current);
        current = parentOf.get(current) ?? null;
    }
    return false;
}
/**
 * Разбор строки быстрой записи: «оформить страховку #дом» превращается в
 * заголовок и тег (3.3.3). Тем же кодом разбирает заголовок `POST /v1/tasks`,
 * поэтому агент и интерфейс понимают ввод одинаково.
 */
export function parseQuickInput(raw) {
    const tags = [];
    const withoutTags = raw.replace(/(^|\s)#([^\s#]+)/g, (_match, space, tag) => {
        const normalized = normalizeTag(tag);
        if (normalized.length > 0 && !tags.includes(normalized)) {
            tags.push(normalized);
        }
        return space;
    });
    return { title: withoutTags.replace(/\s+/g, ' ').trim(), tags };
}
// --------------------------------------------------------------------------
// Номера задач
// --------------------------------------------------------------------------
/** Каноническое отображение номера: `Н-42`, с русской «Н». */
export function formatNumber(number) {
    return `Н-${String(number)}`;
}
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// Дефис допустим только после буквы: голое «-42» — это не ссылка, а мусор.
const NUMBER_RE = /^(?:[нh]\s*-?\s*)?(\d+)$/i;
/**
 * Разбор ссылки на задачу. Принимает `Н-42`, латинское `H-42` и голое `42`:
 * человек и агент не должны думать про раскладку (раздел 3.6 архитектуры).
 */
export function parseRef(raw) {
    const value = raw.trim();
    if (UUID_RE.test(value)) {
        return { kind: 'uuid', id: value.toLowerCase() };
    }
    const match = NUMBER_RE.exec(value);
    if (match?.[1] !== undefined) {
        const number = Number(match[1]);
        if (Number.isSafeInteger(number) && number > 0) {
            return { kind: 'number', number };
        }
    }
    return null;
}
//# sourceMappingURL=rules.js.map